import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Inbox, Layers, Plus } from "lucide-react";
import type { TaskWithFlags } from "@desk/contracts";
import { Segmented } from "@/components/ui/misc";
import { Button } from "@/components/ui/button";
import { ProjectGlyph, SpaceGlyph } from "@/components/icons";
import { locationLabel, useHierarchy, useTaskList, type Hierarchy } from "@/data/hooks";
import { DEFAULT_FILTERS, isClosed, type TaskFilters } from "@/lib/filters";
import { href, setParams, useLocation } from "@/router";
import { useConcept } from "@/features/concept-provider";
import { ui } from "@/features/ui-state";
import { FilterBar } from "@/features/filters/FilterBar";
import { ContainerActions, NewContainerDialog } from "@/features/containers/ContainerActions";
import { CollectionBody, ScreenError, useCollection, useViewMode, ViewToggle } from "./collection";
import { cn, plural } from "@/lib/utils";
import { ContainerGlyph, SpaceIcon } from "@/components/container-icon";
import { ContainerDetails, HeaderMedia } from "@/components/container-details";
import { NAME_MAX, ProjectDeadlineChip, ProjectStateChip, useHeaderIcon, useRenameContainer } from "@/features/containers/HeaderEdits";

/** Tells quick add which destination to preselect while this screen is visible. */
export function useCurrentDestination(dest: { areaId?: string | null; spaceId?: string | null; projectId?: string | null; label: string } | null) {
  const key = dest ? `${dest.areaId}|${dest.spaceId}|${dest.projectId}` : "none";
  useEffect(() => {
    ui.set({ currentDestination: dest });
    return () => ui.set({ currentDestination: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

function relativeLocation(h: Hierarchy, scope: { areaId?: string; spaceId?: string; projectId?: string }) {
  return (t: TaskWithFlags): string | null => {
    if (scope.projectId) return t.projectId === scope.projectId ? null : locationLabel(t, h);
    if (scope.spaceId) {
      if (t.spaceId !== scope.spaceId) return locationLabel(t, h);
      return null;
    }
    if (scope.areaId) {
      if (t.areaId !== scope.areaId) return locationLabel(t, h);
      const s = h.space(t.spaceId);
      const p = h.project(t.projectId);
      return p && s ? `${s.name} › ${p.name}` : (s?.name ?? null);
    }
    return locationLabel(t, h);
  };
}

// ───────────────────────── Inbox ─────────────────────────
const INBOX_DEFAULTS: TaskFilters = { ...DEFAULT_FILTERS, sort: "created" };
const INBOX_BASE = { scope: "inbox" as const };

export function InboxScreen() {
  const { concept } = useConcept();
  useCurrentDestination({ areaId: null, spaceId: null, projectId: null, label: "Inbox" });
  const c = useCollection({ base: INBOX_BASE, defaults: INBOX_DEFAULTS, matchCreate: (t) => !t.areaId && !t.spaceId, hideSnoozed: true, extra: (t) => !t.areaId && !t.spaceId });
  return (
    <>
      <concept.PageHeader
        title="Inbox"
        icon={<Inbox />}
        below={<FilterBar filters={c.filters} onChange={c.setFilters} containers={{}} defaults={INBOX_DEFAULTS} labels={c.labels} sorts={["created", "priority", "deadline", "manual"]} />}
      />
      <ScreenError error={c.error} onRetry={c.retry} />
      <CollectionBody
        tasks={c.tasks}
        view="list"
        filters={c.filters}
        setFilters={c.setFilters}
        location={() => null}
        emptyMessage="Inbox is empty."
        ariaLabel="Inbox tasks"
        onManualOrder={c.setManualOrder}
        status={c.status}
      />
    </>
  );
}

// ───────────────────────── All tasks ─────────────────────────
const ALL_BASE = {};
export function AllTasksScreen() {
  const { concept } = useConcept();
  const h = useHierarchy();
  useCurrentDestination(null);
  const [view, setView] = useViewMode(true);
  const c = useCollection({ base: ALL_BASE, defaults: DEFAULT_FILTERS, matchCreate: () => true });
  const loc = useMemo(() => relativeLocation(h, {}), [h]);
  return (
    <>
      <concept.PageHeader
        title="All tasks"
        icon={<Layers />}
        actions={<ViewToggle value={view} onChange={setView} />}
        below={
          <FilterBar
            filters={c.filters}
            onChange={c.setFilters}
            containers={{ area: true, space: true, project: true }}
            defaults={DEFAULT_FILTERS}
            labels={c.labels}
          />
        }
      />
      <ScreenError error={c.error} onRetry={c.retry} />
      <CollectionBody
        tasks={c.tasks}
        view={view}
        filters={c.filters}
        setFilters={c.setFilters}
        location={loc}
        emptyMessage="No open tasks."
        ariaLabel="All tasks"
        onManualOrder={c.setManualOrder}
        status={c.status}
      />
    </>
  );
}

// ───────────────────────── Area ─────────────────────────
function SpacesOverview({ areaId }: { areaId: string }) {
  const h = useHierarchy();
  const { tasks } = useTaskList({ areaId, includeSnoozed: true, limit: 500 });
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    tasks.filter((t) => !isClosed(t)).forEach((t) => t.spaceId && m.set(t.spaceId, (m.get(t.spaceId) ?? 0) + 1));
    return m;
  }, [tasks]);
  const spaces = h.spacesOf(areaId);
  const [creating, setCreating] = useState(false);
  return (
    <section aria-label="Spaces" className="mb-8">
      <div className="flex flex-col">
        {spaces.map((s) => {
          const projects = h.projectsOf(s.id).filter((p) => p.state !== "completed");
          const resting = h.isResting(s);
          return (
            <a
              key={s.id}
              href={href({ name: "space", id: s.id })}
              className="group/sp flex min-h-12 items-center gap-3 rounded-lg px-2.5 py-2 outline-none hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <SpaceIcon space={s} className="size-7 text-[12px]" />
              <span className="min-w-0 flex-1">
                <span className={cn("block truncate text-[15px] font-medium", resting && "text-muted-foreground")}>{s.name}</span>
                <span className="block truncate text-[12.5px] text-subtle">
                  {[
                    plural(counts.get(s.id) ?? 0, "open task"),
                    projects.length ? plural(projects.length, "project") : null,
                    s.settledAt ? "Settled" : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="tabular text-[12px] text-subtle">{s.prefix}</span>
              <ChevronRight className="size-4 text-subtle opacity-0 group-hover/sp:opacity-100" />
            </a>
          );
        })}
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex h-10 items-center gap-3 rounded-lg px-2.5 text-[14px] text-muted-foreground hover:bg-hover hover:text-foreground"
        >
          <span className="inline-flex size-7 items-center justify-center">
            <Plus className="size-4" />
          </span>
          New space
        </button>
      </div>
      <NewContainerDialog kind="space" parentId={areaId} open={creating} onOpenChange={setCreating} />
    </section>
  );
}

export function AreaScreen({ id }: { id: string }) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const area = h.area(id);
  const loc = useLocation();
  const tab = loc.params.get("tab") === "all" ? "all" : "overview";
  const defaults = useMemo<TaskFilters>(() => ({ ...DEFAULT_FILTERS, areaId: id }), [id]);
  const directDefaults = useMemo<TaskFilters>(() => ({ ...DEFAULT_FILTERS, areaId: id, sort: "priority" }), [id]);
  const base = useMemo(() => ({ areaId: id }), [id]);
  const directBase = useMemo(() => ({ areaId: id, direct: true }), [id]);
  useCurrentDestination(area ? { areaId: id, spaceId: null, projectId: null, label: area.name } : null);
  const [view, setView] = useViewMode(tab === "all");
  const all = useCollection({ base, defaults, matchCreate: (t) => t.areaId === id });
  const direct = useCollection({
    base: directBase,
    defaults: directDefaults,
    matchCreate: (t) => t.areaId === id && !t.spaceId,
    extra: (t) => !t.spaceId,
  });
  const locFn = useMemo(() => relativeLocation(h, { areaId: id }), [h, id]);
  const rename = useRenameContainer(area ? { kind: "area", area } : null);
  const headerIcon = useHeaderIcon(area ? { kind: "area", area } : null);
  if (!area) return h.loaded ? <concept.EmptyState message="This area isn't available. It may be archived." /> : null;
  const tabs = (
    <Segmented
      ariaLabel="Area view"
      size="sm"
      value={tab}
      onChange={(v) => setParams({ tab: v === "all" ? "all" : undefined, view: undefined })}
      options={[
        { value: "overview", label: "Spaces" },
        { value: "all", label: "All tasks" },
      ]}
    />
  );
  return (
    <>
      <concept.PageHeader
        title={area.name}
        icon={<ContainerGlyph of={area} className="size-[22px]" fallback={<SpaceGlyph name={area.name} className="size-[22px] text-[12px]" />} />}
        media={area.headerMediaId ? <HeaderMedia mediaId={area.headerMediaId} /> : undefined}
        details={<ContainerDetails details={area} />}
        accent={area.color}
        onRename={rename}
        renameMaxLength={NAME_MAX.area}
        onIconClick={headerIcon.onIconClick}
        actions={
          <>
            {tabs}
            {tab === "all" && <ViewToggle value={view} onChange={setView} />}
            <ContainerActions target={{ kind: "area", area }} compact={false} />
          </>
        }
        below={
          tab === "all" ? (
            <FilterBar filters={all.filters} onChange={all.setFilters} containers={{ space: true, project: true }} defaults={defaults} labels={all.labels} />
          ) : undefined
        }
      />
      {headerIcon.dialog}
      {tab === "overview" ? (
        <>
          <SpacesOverview areaId={id} />
          <div className="mb-1 flex h-8 items-center gap-2 border-b border-border/70 px-2.5">
            <h2 className="font-sans text-[13.5px] font-semibold tracking-normal text-foreground/85">Tasks in {area.name}</h2>
            <Button size="xs" variant="quiet" className="ml-auto" onClick={() => setParams({ tab: "all" })}>
              All tasks
            </Button>
          </div>
          <ScreenError error={direct.error} onRetry={direct.retry} />
          <CollectionBody
            tasks={direct.tasks}
            view="list"
            filters={direct.filters}
            setFilters={direct.setFilters}
            location={() => null}
            emptyMessage={`No tasks filed directly in ${area.name}.`}
            newTaskPreset={{ areaId: id }}
            ariaLabel={`Tasks in ${area.name}`}
            status={direct.status}
          />
        </>
      ) : (
        <>
          <ScreenError error={all.error} onRetry={all.retry} />
          <CollectionBody
            tasks={all.tasks}
            view={view}
            filters={all.filters}
            setFilters={all.setFilters}
            groupBy="space"
            location={locFn}
            emptyMessage={`No open tasks in ${area.name}.`}
            newTaskPreset={{ areaId: id }}
            ariaLabel={`All tasks in ${area.name}`}
            onManualOrder={all.setManualOrder}
            status={all.status}
          />
        </>
      )}
    </>
  );
}

// ───────────────────────── Space ─────────────────────────
export function SpaceScreen({ id }: { id: string }) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const space = h.space(id);
  const defaults = useMemo<TaskFilters>(() => ({ ...DEFAULT_FILTERS, spaceId: id }), [id]);
  const base = useMemo(() => ({ spaceId: id }), [id]);
  useCurrentDestination(space ? { areaId: space.areaId, spaceId: id, projectId: null, label: space.name } : null);
  const [view, setView] = useViewMode(true);
  const c = useCollection({ base, defaults, matchCreate: (t) => t.spaceId === id });
  const locFn = useMemo(() => relativeLocation(h, { spaceId: id }), [h, id]);
  const rename = useRenameContainer(space ? { kind: "space", space } : null);
  const headerIcon = useHeaderIcon(space ? { kind: "space", space } : null);
  if (!space) return h.loaded ? <concept.EmptyState message="This space isn't available. It may be archived." /> : null;
  return (
    <>
      <concept.PageHeader
        title={space.name}
        icon={<SpaceIcon space={space} className="size-[22px] text-[11px]" />}
        media={space.headerMediaId ? <HeaderMedia mediaId={space.headerMediaId} /> : undefined}
        details={<ContainerDetails details={space} />}
        accent={space.color}
        onRename={rename}
        renameMaxLength={NAME_MAX.space}
        onIconClick={headerIcon.onIconClick}
        actions={
          <>
            {space.settledAt && <span className="text-[12.5px] text-subtle">Settled</span>}
            <ViewToggle value={view} onChange={setView} />
            <ContainerActions target={{ kind: "space", space }} compact={false} />
          </>
        }
        below={<FilterBar filters={c.filters} onChange={c.setFilters} containers={{ project: true }} defaults={defaults} labels={c.labels} />}
      />
      {headerIcon.dialog}
      <ScreenError error={c.error} onRetry={c.retry} />
      <CollectionBody
        tasks={c.tasks}
        view={view}
        filters={c.filters}
        setFilters={c.setFilters}
        groupBy={c.filters.projectId ? "none" : "project"}
        location={locFn}
        emptyMessage={`No open tasks in ${space.name}.`}
        newTaskPreset={{ spaceId: id }}
        ariaLabel={`${space.name} tasks`}
        onManualOrder={c.setManualOrder}
        status={c.status}
      />
    </>
  );
}

// ───────────────────────── Project ─────────────────────────
export function ProjectScreen({ id }: { id: string }) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const project = h.project(id);
  const space = h.space(project?.spaceId);
  const defaults = useMemo<TaskFilters>(() => ({ ...DEFAULT_FILTERS, projectId: id }), [id]);
  const base = useMemo(() => ({ projectId: id }), [id]);
  useCurrentDestination(project ? { areaId: project.areaId, spaceId: project.spaceId, projectId: id, label: project.name } : null);
  const [view, setView] = useViewMode(true);
  const c = useCollection({ base, defaults, matchCreate: (t) => t.projectId === id });
  const rename = useRenameContainer(project ? { kind: "project", project } : null);
  if (!project) return h.loaded ? <concept.EmptyState message="This project isn't available. It may be archived." /> : null;
  return (
    <>
      <concept.PageHeader
        title={project.name}
        icon={<ProjectGlyph state={project.state} className="size-[20px]" />}
        details={<ContainerDetails details={project} />}
        accent={project.color}
        onRename={rename}
        renameMaxLength={NAME_MAX.project}
        actions={
          <>
            {space && (
              <a href={href({ name: "space", id: space.id })} className="text-[13px] text-muted-foreground hover:text-foreground">
                {space.name}
              </a>
            )}
            <ProjectStateChip project={project} />
            <ProjectDeadlineChip project={project} />
            <ViewToggle value={view} onChange={setView} />
            <ContainerActions target={{ kind: "project", project }} compact={false} />
          </>
        }
        below={<FilterBar filters={c.filters} onChange={c.setFilters} containers={{}} defaults={defaults} labels={c.labels} />}
      />
      <ScreenError error={c.error} onRetry={c.retry} />
      <CollectionBody
        tasks={c.tasks}
        view={view}
        filters={c.filters}
        setFilters={c.setFilters}
        location={() => null}
        emptyMessage={`No open tasks in ${project.name}.`}
        newTaskPreset={{ projectId: id }}
        ariaLabel={`${project.name} tasks`}
        onManualOrder={c.setManualOrder}
        status={c.status}
      />
    </>
  );
}
